import './style/salesFilterComponent.scss';

import {AwesomeIcon, generateUUID} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {FilterOperator, SalesColumn} from '../../util/enums';

import {BolmateSelectItem} from '../../util/interfaces';

import {PortalSelectOption, SalesDashboardFilter, SalesDashboardFilterGroup} from '../../util/types';

import {deepClone} from '../../util/utils';

import {BolmateNumberInput, BolmateSelect} from '../controls';

import PortalSelectController from '../controls/portalSelect/portalSelectController';

interface Props {
    onApplyFilters: (filterGroup: SalesDashboardFilterGroup) => void;
    onToggleFilterState: (column: SalesColumn, open: boolean) => void;
    filterGroup: SalesDashboardFilterGroup;
    column: SalesColumn;
}

export default function SalesFilterComponent(props: Props) {
    const {t} = useTranslation();
    const groupTypeItems: Array<BolmateSelectItem> = [
        {label: t('salesDashboardV2.filters.matchAll', 'Match alle regels'), value: 'AND'},
        {label: t('salesDashboardV2.filters.matchAny', 'Match een van de regels'), value: 'OR'}
    ];
    const operatorItems: Array<PortalSelectOption<FilterOperator>> = [
        {value: FilterOperator.EQUALS, label: t('salesDashboardV2.filters.equals', 'Gelijk aan')},
        {value: FilterOperator.NOT_EQUALS, label: t('salesDashboardV2.filters.notEquals', 'Niet gelijk aan')},
        {value: FilterOperator.GT, label: t('salesDashboardV2.filters.gt', 'Groter dan')},
        {value: FilterOperator.GTE, label: t('salesDashboardV2.filters.gte', 'Groter dan of gelijk aan')},
        {value: FilterOperator.LT, label: t('salesDashboardV2.filters.lt', 'Kleiner dan')},
        {value: FilterOperator.LTE, label: t('salesDashboardV2.filters.lte', 'Kleiner dan of gelijk aan')},
        {value: FilterOperator.BETWEEN, label: t('salesDashboardV2.filters.between', 'Tussen')}
    ];
    const {onToggleFilterState, onApplyFilters, column} = props;
    const [open, setOpen] = useState(false);
    const [filterGroup, setFilterGroup] = useState(props.filterGroup);
    const popupRef = useRef<HTMLDivElement>(null);
    const filtersRef = useRef<HTMLDivElement>(null);
    const [filters, hasFilter] = useMemo(() => {
        const filters = filterGroup.filters;
        const hasFilter = filters.some((filter: SalesDashboardFilter) => filter.valueSet);
        return [filters, hasFilter];
    }, [filterGroup]);
    const currencyCol = [
        SalesColumn.REVENUE,
        SalesColumn.ORGANIC_SALES,
        SalesColumn.AD_SPENT,
        SalesColumn.SPONSORED_SALES,
        SalesColumn.CPC,
        SalesColumn.PROD_COSTS,
        SalesColumn.COMMISSION,
        SalesColumn.VAT,
        SalesColumn.PROFIT
    ].includes(column);
    const changed = useRef(false);

    function onOpen() {
        const nextOpen = !open;
        if (nextOpen && !filters.length) onAddFilter();
        window.setTimeout(() => {
            setOpen(nextOpen);
        });
    }

    function onClose() {
        setOpen(false);
    }

    function onAddFilter() {
        const newFilter: SalesDashboardFilter = {id: generateUUID(), operator: FilterOperator.EQUALS, values: []};
        const _filterGroup = deepClone(filterGroup);
        _filterGroup.filters.push(newFilter);
        setFilterGroup(_filterGroup);
        window.setTimeout(() => {
            const container = filtersRef.current;
            container?.scrollTo({top: container.scrollHeight, behavior: 'smooth'});
        });
    }

    function onChangeFilter(
        filterID: string,
        key: keyof SalesDashboardFilter | 'group_type',
        value: number | string | Array<number>
    ) {
        const _filterGroup = deepClone(filterGroup);
        if (key === 'group_type') _filterGroup.group_type = value as SalesDashboardFilterGroup['group_type'];
        const filter = _filterGroup.filters.find((filter: SalesDashboardFilter) => filter.id === filterID);
        if (!filter) return;

        if (!changed.current) changed.current = filter.valueSet || key === 'values';
        _filterGroup.filters.splice(_filterGroup.filters.indexOf(filter), 1, {
            ...filter,
            [key]: value,
            valueSet: filter.valueSet || key === 'values'
        });
        setFilterGroup(_filterGroup);
    }

    function onDeleteFilter(filterID: string) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            const _filterGroup = deepClone(filterGroup);
            if (_filterGroup.filters.length === 1) {
                if (!changed.current) changed.current = _filterGroup.filters[0].valueSet ?? false;
                _filterGroup.filters = [{id: generateUUID(), operator: FilterOperator.EQUALS, values: []}];
            } else {
                const filter = _filterGroup.filters.find((filter: SalesDashboardFilter) => filter.id === filterID);
                if (!filter) return;
                _filterGroup.filters.splice(_filterGroup.filters.indexOf(filter), 1);
                if (!changed.current) changed.current = filter.valueSet ?? false;
            }
            setFilterGroup(_filterGroup);
        };
    }

    function onChangeValue(id: string, index: number) {
        return function (value: number) {
            const filter = filters.find((filter: SalesDashboardFilter) => filter.id === id);
            if (!filter) return;
            filter.values[index] = value;
            onChangeFilter(id, 'values', filter.values);
            changed.current = true;
        };
    }

    function onChangeOperatorSelect(id: string) {
        return function (operator: FilterOperator) {
            onChangeFilter(id, 'operator', operator);
            changed.current = true;
        };
    }

    function onChangeGroupType(value: 'AND' | 'OR') {
        onChangeFilter('', 'group_type', value);
        changed.current = true;
    }

    function renderFilter(filter: SalesDashboardFilter) {
        const value1 = filter.values.length ? filter.values[0] : '';
        return (
            <div key={filter.id} className='sales-filter'>
                <div className='filter-head'>
                    {renderOperatorsSelect(filter)}
                    <AwesomeIcon
                        name='trash-alt'
                        className='delete-icon'
                        onClick={onDeleteFilter(filter.id)}
                        title={t('salesDashboardV2.filters.deleteFilter', 'Filter verwijderen')}
                    />
                </div>
                {filter.operator === FilterOperator.BETWEEN ? (
                    renderBetweenValues(filter)
                ) : (
                    <BolmateNumberInput
                        onChange={onChangeValue(filter.id, 0)}
                        value={value1}
                        asCurrency={currencyCol}
                        className='filter-field'
                        inputProps={{placeholder: t('salesDashboardV2.filters.value', 'Waarde')}}
                        asNumber
                    />
                )}
            </div>
        );
    }

    function renderOperatorsSelect(filter: SalesDashboardFilter) {
        return (
            <PortalSelectController
                onChange={onChangeOperatorSelect(filter.id)}
                items={operatorItems}
                value={filter.operator}
                size='small'
                shape='rounded'
                className='operator-select'
                itemsClassName='operator-select-items'
            />
        );
    }

    function renderBetweenValues(filter: SalesDashboardFilter) {
        const value1 = filter.values.length ? filter.values[0] : '';
        const value2 = filter.values.length > 1 ? filter.values[1] : '';
        return (
            <div className='between-values'>
                <BolmateNumberInput
                    onChange={onChangeValue(filter.id, 0)}
                    value={value1}
                    asCurrency={currencyCol}
                    className='filter-field'
                    inputProps={{placeholder: 'Waarde'}}
                    asNumber
                />
                <span>{t('salesDashboardV2.filters.and', 'en')}</span>
                <BolmateNumberInput
                    onChange={onChangeValue(filter.id, 1)}
                    value={value2}
                    asCurrency={currencyCol}
                    className='filter-field'
                    inputProps={{placeholder: 'Waarde'}}
                    asNumber
                />
            </div>
        );
    }

    useEffect(() => {
        onToggleFilterState(column, open);
        if (open) changed.current = false;
        else if (changed.current) onApplyFilters(filterGroup);
    }, [open]);

    useEffect(() => {
        setFilterGroup(props.filterGroup);
    }, [props.filterGroup]);

    return (
        <div className='sales-filter-component'>
            <AwesomeIcon name='filter' className={classNames('filter-icon', hasFilter && 'active')} onClick={onOpen} />
            <div className={classNames('column-filter', open && 'open')} ref={popupRef}>
                <BolmateSelect
                    items={groupTypeItems}
                    value={filterGroup.group_type}
                    onChange={onChangeGroupType}
                    className='filter-field'
                />
                <div className='filters-container' ref={filtersRef}>
                    {filters.map(renderFilter)}
                </div>
                <div className='add-filter-container' onClick={onAddFilter}>
                    <AwesomeIcon name='plus' className='btn-icon' />
                    <label className='add-filter-label'>
                        {t('salesDashboardV2.filters.addFilter', 'Filter toevoegen')}
                    </label>
                </div>
                <div className='apply-container' onClick={onClose}>
                    <AwesomeIcon name='check' className='btn-icon' />
                    <label className='add-filter-label'>{t('salesDashboardV2.filters.apply', 'Toepassen')}</label>
                </div>
            </div>
        </div>
    );
}

SalesFilterComponent.displayName = 'SalesFilterController';
